import { SyntheticEvent, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { capitalCase } from 'change-case';
// @mui
import { Box, Button, Container, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// components
import { Page, HeaderBreadcrumbs, Iconify } from '@/components/';
import {
  ClientBookings,
  ClientAdminReminders,
  ClientAutomationRules,
  ClientCleanerRestrictions,
  ClientDetails,
  ClientWallet,
  ClientReviews,
  ClientPrices,
  ClientTransactions,
} from '../components';
//  hooks + utils
import useSettings from '@/hooks/useSettings';
import useTabs from '@/hooks/useTabs';
import useToggle from '@/hooks/useToggle';
import { PATH_USER } from '@/features/app';
import { PATH_CLIENT_MANAGEMENT, clientManagementPath } from '../routes/paths';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { PORTAL_BASE_URL } from '@/config';
import { startUserImpersonation } from '@/features/users/shared/api';
import {
  UserCommunications,
  UserSessions,
} from '@/features/users/shared/components';
import { useGetAccess } from '@/hooks/useAccess';

// ----------------------------------------------------------------------

export default function ClientCreateEdit() {
  const { themeStretch } = useSettings();
  const { userId, tabSid } = useParams();
  const { currentTab, onChangeTab, setCurrentTab } = useTabs('details');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loginAsAccess = useGetAccess('login_as');

  useEffect(() => {
    tabSid && setCurrentTab(tabSid);
  }, [tabSid, setCurrentTab]);

  const { errorMsg, successMsg } = useSnackbarMsg();

  const impersonationMutation = useMutation({
    mutationFn: (clientId: number) => startUserImpersonation(clientId),
    onSuccess: (impersonation, client) => {
      queryClient.setQueryData(['client-impersonation', client], impersonation);
    },
    onError: (error: unknown) => {
      errorMsg(error, 'Failed to start shadow impersonation session');
    },
  });

  const handleLoginAsClient = async () => {
    if (!userId) {
      errorMsg('Client id is not available', 'Unable to impersonate client');
      return;
    }

    const clientNumericId = Number(userId);

    if (Number.isNaN(clientNumericId)) {
      errorMsg('Invalid client id', 'Unable to impersonate client');
      return;
    }

    try {
      const impersonation = await impersonationMutation.mutateAsync(
        clientNumericId
      );

      if (!impersonation.code) {
        errorMsg(
          'Unable to generate an impersonation code. Please try again later.'
        );
        return;
      }

      let portalUrl: URL;

      try {
        portalUrl = new URL(PORTAL_BASE_URL);
      } catch {
        portalUrl = new URL(PORTAL_BASE_URL, window.location.origin);
      }

      portalUrl.searchParams.set('impersonationCode', impersonation.code);

      const newWindow = window.open(
        portalUrl.toString(),
        '_blank',
        'noopener,noreferrer'
      );

      if (!newWindow) {
        errorMsg(
          'Unable to open the client portal. Please allow pop-ups and try again.'
        );
        return;
      }

      newWindow.focus();
      successMsg('Opening client portal in a new tab');
    } catch (error) {
      // handled by mutation onError
    }
  };

  const handleTabChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    onChangeTab(event, newValue);
    navigate(
      clientManagementPath('edit', { userId: String(userId), tabSid: newValue })
    );
  };

  const {
    toggle: isCustomPriceDialogOpen,
    onOpen: openCustomPriceDialog,
    onClose: closeCustomPriceDialog,
  } = useToggle();

  const CLIENT_PROFILE_TABS = [
    {
      value: 'details',
      component: <ClientDetails />,
      visible: true,
    },
    {
      value: 'admin-reminders',
      label: 'Admin reminders',
      component: <ClientAdminReminders />,
      visible: !!userId,
    },
    {
      value: 'automation-rules',
      label: 'Automation rules',
      component: <ClientAutomationRules />,
      visible: !!userId,
    },
    {
      value: 'bookings',
      component: <ClientBookings />,
      visible: !!userId,
    },
    {
      value: 'transactions',
      component: <ClientTransactions />,
      visible: !!userId,
    },
    {
      value: 'sessions',
      label: 'Login Sessions',
      component: <UserSessions />,
      visible: !!userId,
    },
    {
      value: 'wallet',
      component: <ClientWallet />,
      visible: !!userId,
    },
    {
      value: 'reviews',
      component: <ClientReviews />,
      visible: !!userId,
    },
    {
      value: 'communications',
      component: <UserCommunications />,
      visible: !!userId,
    },
    {
      value: 'restricted-cleaners',
      label: 'Restricted cleaners',
      component: <ClientCleanerRestrictions />,
      visible: !!userId,
    },
    {
      value: 'prices',
      component: (
        <ClientPrices
          isCustomPriceDialogOpen={isCustomPriceDialogOpen}
          closeCustomPriceDialog={closeCustomPriceDialog}
        />
      ),
      visible: !!userId,
    },
  ];

  return (
    <Page title={`Clients: ${!userId ? 'Create new' : 'Edit client'}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={!userId ? 'Create a new client' : 'Edit client'}
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Clients', href: PATH_CLIENT_MANAGEMENT.root },
            { name: !userId ? 'New client' : 'Edit client' },
          ]}
          action={
            userId ? (
              <Stack direction="row" spacing={1}>
                {loginAsAccess.canUpdate && (
                  <Button
                    variant="outlined"
                    onClick={handleLoginAsClient}
                    disabled={impersonationMutation.isLoading}
                  >
                    {impersonationMutation.isLoading
                      ? 'Starting session...'
                      : 'Login as client'}
                  </Button>
                )}
                {currentTab === 'prices' && (
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon={'eva:plus-fill'} />}
                    onClick={openCustomPriceDialog}
                  >
                    Add Custom Price
                  </Button>
                )}
              </Stack>
            ) : (
              currentTab === 'prices' && (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                  onClick={openCustomPriceDialog}
                >
                  Add Custom Price
                </Button>
              )
            )
          }
        />

        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={currentTab}
          onChange={handleTabChange}
          sx={{ mb: 1 }}
          aria-label="profile tabs"
        >
          {CLIENT_PROFILE_TABS.map(
            (tab) =>
              tab.visible && (
                <Tab
                  disableRipple
                  key={tab.value}
                  value={tab.value}
                  label={tab.label ?? capitalCase(tab.value)}
                />
              )
          )}
        </Tabs>

        {CLIENT_PROFILE_TABS.map(
          (tab) =>
            tab.value === currentTab && (
              <Box key={tab.value} sx={{ width: '100%' }}>
                {tab.component}
              </Box>
            )
        )}
      </Container>
    </Page>
  );
}
